'use client';

import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { reverseGeocodeLocation } from 'src/hooks/routes/utils';
import type { RouteStartingPoint } from 'src/hooks/routes/useRouteDetailLogic';
import { Card, CardContent, CardTitle } from 'src/components/Routes/components/ui';

export interface StartingLocationCardProps {
  startingPoint: RouteStartingPoint | null;
}

const StartingLocationCard: React.FC<StartingLocationCardProps> = ({
  startingPoint,
}) => {
  const lat = startingPoint?.lat ?? null;
  const lng = startingPoint?.lng ?? null;
  const isPersisted = startingPoint?.isPersisted ?? false;
  const [address, setAddress] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    setAddress(null);
    setResolved(false);
    if (lat === null || lng === null) return;

    let cancelled = false;
    reverseGeocodeLocation({ lat, lng }).then((result) => {
      if (cancelled) return;
      setAddress(result);
      setResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, [lat, lng]);

  // A saved origin never reads "Current Location", which is reserved for the
  // live position; it falls back to raw coordinates when no address resolves.
  let text = 'Location unavailable';
  if (lat !== null && lng !== null) {
    text =
      address ??
      (isPersisted
        ? `${lat.toFixed(4)}, ${lng.toFixed(4)}`
        : 'Current Location');
  }

  return (
    <Card className="bg-neutral-50" data-testid="starting-location-card">
      <CardContent className="flex-row items-center gap-3">
        <Icon icon={faLocationDot} className="text-sky-600" />
        <div className="flex flex-col min-w-0">
          <CardTitle className="text-base">Starting Location</CardTitle>
          {isPersisted && !resolved ? (
            <div
              className="h-3 w-32 rounded-full bg-neutral-200 animate-pulse motion-reduce:animate-none"
              data-testid="starting-location-skeleton"
            />
          ) : (
            <span className="text-sm text-neutral-500 truncate">{text}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StartingLocationCard;
